import React, { useState, useEffect, useRef } from 'react';
import PlatformIcon from '../../components/common/PlatformIcon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { FaFacebook, FaLinkedin, FaTwitter, FaReddit } from 'react-icons/fa';

interface Post {
  content: string;
  hashtags: string[];
  bestTimeToPost: string;
  explanation: string;
}

type Platform = 'facebook' | 'linkedin' | 'twitter' | 'reddit';

const SocialPostGenerator: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [platform, setPlatform] = useState<Platform>('facebook');
  const [topic, setTopic] = useState('');
  const [postFormat, setPostFormat] = useState('');
  const [purpose, setPurpose] = useState('');
  const [tone, setTone] = useState('');
  const [audience, setAudience] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [posts, setPosts] = useState<Post[]>([]);

  // Example data for quick-fill based on selected platform
  const getExampleData = () => {
    const examples = {
      facebook: {
        topic: 'Behind-the-scenes from our workshop',
        postFormat: 'Carousel idea',
        purpose: 'Boost engagement',
        tone: 'Authentic',
        audience: 'Current and potential customers',
        keywords: ['handmade', 'craftmanship', 'sustainable']
      },
      linkedin: {
        topic: 'How we cut meeting time by 70%',
        postFormat: 'Professional achievement',
        purpose: 'Educate audience',
        tone: 'Confident',
        audience: 'Team leaders and productivity enthusiasts',
        keywords: ['productivity', 'teamwork', 'leadership']
      },
      twitter: {
        topic: 'We just shipped our 10,000th order!',
        postFormat: 'Announcement',
        purpose: 'Increase brand awareness',
        tone: 'Excited',
        audience: 'Current and potential customers',
        keywords: ['milestone', 'customerservice', 'smallbusiness']
      },
      reddit: {
        topic: 'I\'ve been designing UX for mobile games for 5 years, AMA',
        postFormat: 'AMA format',
        purpose: 'Build community',
        tone: 'Casual',
        audience: 'Design community and mobile gamers',
        keywords: ['gamedev', 'UX', 'design']
      }
    };
    
    return examples[platform];
  };

  // Get platform-appropriate post format options with descriptions
  const getPostFormatOptions = () => {
    type PlatformFormats = {
      [key: string]: {
        [key: string]: string;
      }
    };
    
    const formatDetails: PlatformFormats = {
      facebook: {
        'Standard post': 'Classic text with optional image',
        'Question post': 'Post that asks followers to engage',
        'Poll': 'Multiple-choice question for audience',
        'Event announcement': 'Promote upcoming events or webinars',
        'Carousel idea': 'Series of images with swipeable narrative',
        'Product showcase': 'Highlight features and benefits',
        'Customer story': 'Share success stories and testimonials'
      },
      linkedin: {
        'Standard post': 'Professional update with clear value',
        'Question post': 'Industry-relevant question to generate discussion',
        'Poll': 'Professional opinion polling',
        'Article summary': 'Key insights from longer content',
        'Professional achievement': 'Career milestone or company win',
        'Company update': 'News about your organization',
        'Industry insight': 'Thought leadership and analysis'
      },
      twitter: {
        'Standard post': 'Concise update under 280 chars',
        'Question post': 'Engaging question for quick replies',
        'Poll': 'Quick opinion gathering',
        'Announcement': 'Company news or product updates',
        'Thread idea': 'First tweet in a connected series',
        'News commentary': 'Your take on industry developments',
        'Trend participation': 'Join relevant trending topics'
      },
      reddit: {
        'Discussion starter': 'Open-ended topic to generate conversation',
        'Question/advice seeking': 'Specific query for community help',
        'Information sharing': 'Valuable resource or insight for the community',
        'Community poll': 'Get opinions from subreddit members',
        'AMA format': 'Answer questions based on your expertise'
      }
    };
    
    const options = Object.keys(formatDetails[platform] || {});
    return options;
  };
  
  // Get description for selected format
  const getFormatDescription = (format: string) => {
    if (!format) return '';
    
    type PlatformDescriptions = {
      [key: string]: {
        [key: string]: string;
      }
    };
    
    const descriptions: PlatformDescriptions = {
      facebook: {
        'Standard post': 'Classic text with optional image',
        'Question post': 'Post that asks followers to engage',
        'Poll': 'Multiple-choice question for audience',
        'Event announcement': 'Promote upcoming events or webinars',
        'Carousel idea': 'Series of images with swipeable narrative',
        'Product showcase': 'Highlight features and benefits',
        'Customer story': 'Share success stories and testimonials'
      },
      linkedin: {
        'Standard post': 'Professional update with clear value',
        'Question post': 'Industry-relevant question to generate discussion',
        'Poll': 'Professional opinion polling',
        'Article summary': 'Key insights from longer content',
        'Professional achievement': 'Career milestone or company win',
        'Company update': 'News about your organization',
        'Industry insight': 'Thought leadership and analysis'
      },
      twitter: {
        'Standard post': 'Concise update under 280 chars',
        'Question post': 'Engaging question for quick replies',
        'Poll': 'Quick opinion gathering',
        'Announcement': 'Company news or product updates',
        'Thread idea': 'First tweet in a connected series',
        'News commentary': 'Your take on industry developments',
        'Trend participation': 'Join relevant trending topics'
      },
      reddit: {
        'Discussion starter': 'Open-ended topic to generate conversation',
        'Question/advice seeking': 'Specific query for community help',
        'Information sharing': 'Valuable resource or insight for the community',
        'Community poll': 'Get opinions from subreddit members',
        'AMA format': 'Answer questions based on your expertise'
      }
    };
    
    return descriptions[platform]?.[format] || '';
  };
  
  const handleUseExample = () => {
    const example = getExampleData();
    setTopic(example.topic);
    setPostFormat(example.postFormat);
    setPurpose(example.purpose);
    setTone(example.tone);
    setAudience(example.audience);
    setKeywords(example.keywords);
  };

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!platform || !topic || !purpose) {
      toast.error('Please fill in all required fields: Platform, Topic, and Purpose');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      // Combine format with topic if format is selected
      const enhancedTopic = postFormat ? `[${postFormat}] ${topic}` : topic;
      
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/social-post`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId, 
            platform, 
            topic: enhancedTopic, 
            purpose, 
            tone, 
            audience, 
            keywords 
          }),
        }
      );
      
      const data = await response.json();
      setPosts(data.posts);
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} posts generated successfully!`);
    } catch (error) {
      console.error(`Error generating ${platform} posts:`, error);
      toast.error(`Failed to generate ${platform} posts. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: Platform): JSX.Element | null => {
    switch (platform) {
      case 'facebook':
        return <FaFacebook className="text-blue-600" />;
      case 'linkedin':
        return <FaLinkedin className="text-blue-700" />;
      case 'twitter':
        return <FaTwitter className="text-blue-400" />;
      case 'reddit':
        return <FaReddit className="text-orange-600" />;
      default:
        return null;
    }
  };

  const getPlatformColor = () => {
    // Always return 'blue' for submission button color to ensure consistency
    return 'blue';
    
    // Previously was using different colors per platform:
    // switch (platform) {
    //   case 'facebook':
    //     return 'blue';
    //   case 'linkedin':
    //     return 'indigo';
    //   case 'twitter':
    //     return 'sky';
    //   case 'reddit':
    //     return 'orange';
    //   default:
    //     return 'blue';
    // }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const color = getPlatformColor();

  return (
    <div className="container mx-auto px-4 py-10 relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-indigo-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-10">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Social Media Post Generator</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Create engaging social media content optimized for your chosen platform.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 to-blue-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Generate Social Media Posts</h2>
              <button
                type="button"
                className="text-xs px-3 py-1.5 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 font-medium text-indigo-700 shadow-sm hover:shadow"
                onClick={handleUseExample}
              >
                <span className="flex items-center">
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Use Example
                </span>
              </button>
            </div>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Platform <span className="text-indigo-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => setPlatform('facebook')}
                  className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    platform === 'facebook' 
                      ? 'bg-gradient-to-r from-blue-500/80 to-blue-600/80 text-white shadow-lg' 
                      : 'backdrop-blur-sm bg-white/30 border border-white/40 text-blue-700 hover:bg-white/40 shadow-md'
                  }`}
                >
                  <FaFacebook className="mr-2 text-lg" /> Facebook
                </button>
                <button
                  type="button"
                  onClick={() => setPlatform('linkedin')}
                  className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    platform === 'linkedin' 
                      ? 'bg-gradient-to-r from-indigo-500/80 to-indigo-600/80 text-white shadow-lg' 
                      : 'backdrop-blur-sm bg-white/30 border border-white/40 text-indigo-700 hover:bg-white/40 shadow-md'
                  }`}
                >
                  <FaLinkedin className="mr-2 text-lg" /> LinkedIn
                </button>
                <button
                  type="button"
                  onClick={() => setPlatform('twitter')}
                  className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    platform === 'twitter' 
                      ? 'bg-gradient-to-r from-sky-500/80 to-sky-600/80 text-white shadow-lg' 
                      : 'backdrop-blur-sm bg-white/30 border border-white/40 text-sky-700 hover:bg-white/40 shadow-md'
                  }`}
                >
                  <FaTwitter className="mr-2 text-lg" /> Twitter
                </button>
                <button
                  type="button"
                  onClick={() => setPlatform('reddit')}
                  className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 ${
                    platform === 'reddit' 
                      ? 'bg-gradient-to-r from-orange-500/80 to-orange-600/80 text-white shadow-lg' 
                      : 'backdrop-blur-sm bg-white/30 border border-white/40 text-orange-700 hover:bg-white/40 shadow-md'
                  }`}
                >
                  <FaReddit className="mr-2 text-lg" /> Reddit
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="topic" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Topic <span className="text-indigo-500">*</span>
              </label>
              <input
                type="text"
                id="topic"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Product launch, Industry news, Company milestone"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="postFormat" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Post Format
              </label>
              <select
                id="postFormat"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                value={postFormat}
                onChange={(e) => setPostFormat(e.target.value)}
              >
                <option value="">Select a format (optional)</option>
                {getPostFormatOptions().map((format) => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
              {postFormat && (
                <p className="mt-2 text-sm text-indigo-600/70 italic bg-white/20 backdrop-blur-sm p-2 rounded-lg">{getFormatDescription(postFormat)}</p>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="purpose" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Purpose <span className="text-indigo-500">*</span>
              </label>
              <select
                id="purpose"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                required
              >
                <option value="">Select a purpose</option>
                <option value="Increase brand awareness">Increase brand awareness</option>
                <option value="Drive website traffic">Drive website traffic</option>
                <option value="Generate leads">Generate leads</option>
                <option value="Boost engagement">Boost engagement</option>
                <option value="Promote a product/service">Promote a product/service</option>
                <option value="Share company news">Share company news</option>
                <option value="Educate audience">Educate audience</option>
                <option value="Build community">Build community</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label htmlFor="tone" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Tone
              </label>
              <select
                id="tone"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option value="">Select a tone (optional)</option>
                <option value="Professional">Professional</option>
                <option value="Casual">Casual</option>
                <option value="Friendly">Friendly</option>
                <option value="Humorous">Humorous</option>
                <option value="Inspirational">Inspirational</option>
                <option value="Informative">Informative</option>
                <option value="Authoritative">Authoritative</option>
                <option value="Conversational">Conversational</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label htmlFor="audience" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Target Audience
              </label>
              <input
                type="text"
                id="audience"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="e.g. Small business owners, Marketing professionals, Tech enthusiasts"
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="keywords" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-2">
                Keywords
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="keywords"
                  className="flex-grow px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-l-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="e.g. innovation, growth, strategy"
                />
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white px-4 py-3 rounded-r-xl hover:from-indigo-600 hover:to-blue-600 shadow-sm hover:shadow transition-all duration-200"
                >
                  Add
                </button>
              </div>
              
              {keywords.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-indigo-100 to-blue-100 text-indigo-800 text-sm backdrop-blur-sm shadow-sm"
                    >
                      {keyword}
                      <button
                        type="button"
                        className="ml-1.5 inline-flex text-indigo-500 hover:text-indigo-700 focus:outline-none transition-colors"
                        onClick={() => handleRemoveKeyword(keyword)}
                      >
                        <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 rounded-xl text-white font-medium shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-0.5 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </span>
              ) : `Generate ${platform.charAt(0).toUpperCase() + platform.slice(1)} Posts`}
            </button>
          </form>
          </div>
        </div>
        
        <div className="h-full">
          {posts.length > 0 ? (
            <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8 h-full">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 to-blue-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="text-3xl mr-3 ${platform === 'facebook' ? 'text-blue-500' : platform === 'linkedin' ? 'text-indigo-500' : platform === 'twitter' ? 'text-sky-500' : 'text-orange-500'}">{getPlatformIcon(platform)}</div>
                  <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">{platform.charAt(0).toUpperCase() + platform.slice(1)} Posts</h2>
                </div>
                
                <div className="space-y-8">
                  {posts.map((post, index) => (
                    <div key={index} className="bg-white/50 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 relative">
                      <div className="absolute top-3 right-3">
                        <button
                          onClick={() => {
                            const content = `${post.content}

${post.hashtags.map(tag => `#${tag}`).join(' ')}`;
                            navigator.clipboard.writeText(content);
                            toast.success('Post copied to clipboard!');
                          }}
                          className="bg-white/60 hover:bg-white/90 text-indigo-600 rounded-lg px-3 py-1.5 text-xs font-medium shadow-sm hover:shadow transition-all duration-200 flex items-center"
                        >
                          <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                          Copy
                        </button>
                      </div>
                      <div className="mb-4 pr-20">
                        <p className="text-gray-800 whitespace-pre-line mb-3 leading-relaxed">{post.content}</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {post.hashtags.map((tag, tagIndex) => (
                            <span key={tagIndex} className="px-2 py-1 rounded-lg bg-gradient-to-r ${
                              platform === 'facebook' ? 'from-blue-100 to-blue-200 text-blue-700' :
                              platform === 'linkedin' ? 'from-indigo-100 to-indigo-200 text-indigo-700' :
                              platform === 'twitter' ? 'from-sky-100 to-sky-200 text-sky-700' :
                              'from-orange-100 to-orange-200 text-orange-700'
                            } text-sm font-medium shadow-sm"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-indigo-600 font-medium flex items-center">
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Best time to post: {post.bestTimeToPost}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg shadow-sm border border-white/40">
                        <p className="font-semibold mb-2 text-indigo-800">Why this works:</p>
                        <p className="text-gray-700">{post.explanation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col backdrop-blur-sm bg-white/10 border border-white/20 border-dashed rounded-2xl p-8">
              <div className="flex items-center mb-5">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br ${
                  platform === 'facebook' ? 'from-blue-100 to-blue-200' :
                  platform === 'linkedin' ? 'from-indigo-100 to-indigo-200' :
                  platform === 'twitter' ? 'from-sky-100 to-sky-200' :
                  'from-orange-100 to-orange-200'
                } flex items-center justify-center mr-3 shadow-sm">
                  <div className="text-2xl ${
                    platform === 'facebook' ? 'text-blue-500' :
                    platform === 'linkedin' ? 'text-indigo-500' :
                    platform === 'twitter' ? 'text-sky-500' :
                    'text-orange-500'
                  }">{getPlatformIcon(platform)}</div>
                </div>
                <h3 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Example {platform.charAt(0).toUpperCase() + platform.slice(1)} Post</h3>
              </div>
              
              <div className="flex-grow bg-white/40 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm">
                <div className="mb-4">
                  {platform === 'facebook' && (
                    <p className="text-gray-800 mb-3 leading-relaxed">Ever wonder where your coffee comes from? 👀 Swipe through to see the journey from farm to cup! We visited our suppliers in Colombia last week and captured the entire process. The fourth photo might surprise you! ☕️ #BehindTheScenes</p>
                  )}
                  {platform === 'linkedin' && (
                    <p className="text-gray-800 mb-3 leading-relaxed">We eliminated 80% of our meetings and productivity jumped 35%. Here's what we did:

1) Made 15 mins the default meeting length
2) Required agendas for everything
3) Implemented "No-Meeting Thursdays"

The biggest win? Team happiness scores are up 40%. What's your team's approach to meeting culture?</p>
                  )}
                  {platform === 'twitter' && (
                    <p className="text-gray-800 mb-3 leading-relaxed">When we started in our garage 3 years ago, we never imagined shipping our 10,000th order today! 📦✨ To the customer who just ordered our limited edition set: check your box for a special surprise! #SmallBizMilestone</p>
                  )}
                  {platform === 'reddit' && (
                    <p className="text-gray-800 mb-3 leading-relaxed">I've designed UX for top mobile games with 10M+ downloads. Most game devs still get these 3 things wrong:

1. Tutorial friction
2. First-time user rewards
3. Session ending hooks

I've worked on titles like [game] and [game]. Ask me anything about mobile game UX!</p>
                  )}
                  
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {platform === 'facebook' && (
                      <>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 text-sm font-medium shadow-sm">#BehindTheScenes</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 text-sm font-medium shadow-sm">#CoffeeLover</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 text-sm font-medium shadow-sm">#FarmToTable</span>
                      </>
                    )}
                    {platform === 'linkedin' && (
                      <>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-700 text-sm font-medium shadow-sm">#ProductivityHacks</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-700 text-sm font-medium shadow-sm">#RemoteWork</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-700 text-sm font-medium shadow-sm">#CompanyCulture</span>
                      </>
                    )}
                    {platform === 'twitter' && (
                      <>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-sky-100 to-sky-200 text-sky-700 text-sm font-medium shadow-sm">#SmallBizMilestone</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-sky-100 to-sky-200 text-sky-700 text-sm font-medium shadow-sm">#ThankYouCustomers</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-sky-100 to-sky-200 text-sky-700 text-sm font-medium shadow-sm">#EntrepreneurLife</span>
                      </>
                    )}
                    {platform === 'reddit' && (
                      <>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 text-sm font-medium shadow-sm">#GameDev</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 text-sm font-medium shadow-sm">#UXDesign</span>
                        <span className="px-2 py-1 rounded-lg bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 text-sm font-medium shadow-sm">#MobileGaming</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-indigo-600 font-medium flex items-center">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Best time to post: {platform === 'facebook' ? 'Weekdays 1-4 PM' : platform === 'linkedin' ? 'Tuesday-Thursday 9-11 AM' : platform === 'twitter' ? 'Weekdays 12-3 PM' : 'Weekdays 7-9 AM'}
                  </p>
                </div>
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg shadow-sm border border-white/40 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <p className="font-medium text-indigo-700">Fill out the form and click "Generate {platform.charAt(0).toUpperCase() + platform.slice(1)} Posts" to create your own!</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-12 relative overflow-hidden backdrop-blur-sm bg-gradient-to-br from-indigo-50/40 to-blue-50/40 border border-white/30 rounded-2xl shadow-lg p-8">
        {/* Small animated orbs */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-indigo-200 to-blue-200 rounded-full opacity-20 blur-2xl animate-pulse"></div>
        <div className="absolute -bottom-12 -left-10 w-48 h-48 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full opacity-20 blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 mb-4">Platform-Specific Tips</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-1">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 shadow-sm">
                  <FaFacebook className="text-blue-600 text-xl" />
                </div>
                <h3 className="font-semibold text-blue-700">Facebook</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Best for: Building community and sharing diverse content</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Optimal length: 40-80 characters</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Include visuals when possible</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>Ask questions to boost engagement</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-1">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 shadow-sm">
                  <FaLinkedin className="text-indigo-600 text-xl" />
                </div>
                <h3 className="font-semibold text-indigo-700">LinkedIn</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-2">•</span>
                  <span>Best for: Professional content and thought leadership</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-2">•</span>
                  <span>Optimal length: 100-150 characters</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-2">•</span>
                  <span>Use industry-specific hashtags</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-500 mr-2">•</span>
                  <span>Share insights and professional achievements</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-1">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-3 shadow-sm">
                  <FaTwitter className="text-sky-500 text-xl" />
                </div>
                <h3 className="font-semibold text-sky-700">Twitter</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <span className="text-sky-500 mr-2">•</span>
                  <span>Best for: News, trends, and quick updates</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sky-500 mr-2">•</span>
                  <span>Optimal length: Under 280 characters</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sky-500 mr-2">•</span>
                  <span>Use 1-2 relevant hashtags</span>
                </li>
                <li className="flex items-start">
                  <span className="text-sky-500 mr-2">•</span>
                  <span>Join trending conversations</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-5 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-1">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 shadow-sm">
                  <FaReddit className="text-orange-500 text-xl" />
                </div>
                <h3 className="font-semibold text-orange-700">Reddit</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  <span>Best for: In-depth discussions and niche communities</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  <span>Focus on providing value, not promotion</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  <span>Follow subreddit rules carefully</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">•</span>
                  <span>Engage authentically with comments</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialPostGenerator;
